import { Alert, AlertTitle, Box, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow, TextField, MenuItem, Stack, Button, CircularProgress } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import PageHeader from "src/components/page-header";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import NoneFoundReport from "src/components/none-found-report";
import { TruxTableCell } from "src/components/trux-table-cell";
import { enqueueSnackbar } from "notistack";
import { EldVehicle } from "src/models";
import Iconify from "src/components/iconify";
import { DownloadExcelButton, ReportFilter } from "../reports/runner/report-runner";

export type PerHistoryEventLine = {
  time: string;
  lat: number;
  lon: number;
  state: string;
  odometer: number | null;
  engineHours: number | null;
  fuelLevel: number | null;
  speed: number | null;
  mac: string;
  vin: string;
  reason: string;
  location: string;
}

const DeviceHistoryLineReport = memo(({deviceHistoryLines} : {deviceHistoryLines: PerHistoryEventLine[]}) => {
    const { sortedList, sortData } = useSort(deviceHistoryLines);
      if (!sortedList) return null;
    
      return (
        <Card>
          <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableHeader sortData={sortData} sortKey="time">DATE / TIME</SortableHeader>
                  <SortableHeader sortData={sortData} sortKey="location">Location</SortableHeader>
                  <SortableHeader sortData={sortData} sortKey="lat">LATITUDE</SortableHeader>
                  <SortableHeader sortData={sortData} sortKey="lon">LONGITUDE</SortableHeader>
                  <SortableHeader sortData={sortData} sortKey="state">STATE</SortableHeader>
                  <SortableHeader sortData={sortData} sortKey="speed">SPEED (mph)</SortableHeader>
                  <SortableHeader sortData={sortData} sortKey="odometer">ODOMETER</SortableHeader>
                  <SortableHeader sortData={sortData} sortKey="engineHours">ENGINE HOURS</SortableHeader>
                  <SortableHeader sortData={sortData} sortKey="fuelLevel">FUEL %</SortableHeader>
                  <SortableHeader sortData={sortData} sortKey="mac">ELD DEVICE MAC</SortableHeader>
                  <SortableHeader sortData={sortData} sortKey="vin">VIN</SortableHeader>
                  <SortableHeader sortData={sortData} sortKey="reason">REASON</SortableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedList.map((line, i) => (
                    <TableRow key={i}>
                      <TruxTableCell>{line.time}</TruxTableCell>
                      <TruxTableCell>{line.location}</TruxTableCell>
                      <TruxTableCell>{line.lat?.toFixed(4)}</TruxTableCell>
                      <TruxTableCell>{line.lon?.toFixed(4)}</TruxTableCell>
                      <TruxTableCell>{line.state}</TruxTableCell>
                      <TruxTableCell>{line.speed}</TruxTableCell>
                      <TruxTableCell>{line.odometer?.toFixed(2)}</TruxTableCell>
                      <TruxTableCell>{line.engineHours}</TruxTableCell>
                      <TruxTableCell>{line.fuelLevel}</TruxTableCell>
                      <TruxTableCell>{line.mac}</TruxTableCell>
                      <TruxTableCell>{line.vin}</TruxTableCell>
                      <TruxTableCell>{line.reason}</TruxTableCell>
                  </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      );
})

type PageModel = {
  lines?: PerHistoryEventLine[],
  eldVehicles?: EldVehicle[],
}

export default function Page() {
  const settings = useSettingsContext();

  const currentRequest = useRef({} as any);

  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  const [eldVehicleId, setEldVehicleId] = useState<number | null>(null);
  const [usState, setUsState] = useState<string>("");
  const [states, setStates] = useState<string[]>([]);
  const [statesLoading, setStatesLoading] = useState(false);

  useEffect(() => {
    if (eldVehicleId && fromDate && toDate) {
      setStatesLoading(true);
      setUsState(""); 
      axios.get(`${endpoints.reports.historyLines.states}?eldVehicleId=${eldVehicleId}&fromDate=${fromDate}&toDate=${toDate}`)
        .then(response => {
          setStates(response.data || []);
          setStatesLoading(false);
        })
        .catch(() => {
          setStates([]);
          setStatesLoading(false);
        });
    } else {
      setStates([]);
      setUsState("");
    }
  }, [eldVehicleId, fromDate, toDate]);

  const dataLoader = useCallback(() => ({
    lines: (currentRequest.current as any).ran && axios.post(
      endpoints.reports.historyLines.data,
      JSON.stringify(currentRequest.current)
    ).then(response => response.data),
    eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
  }), []);
  const {
    data,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  const vehicles = useMemo(() => 
    data?.eldVehicles?.filter(v => v.active).map(v => ({ id: v.id, vehicleUnitNumber: v.vehicleUnitNumber })) || [],
    [data?.eldVehicles]
  );

  const filters: ReportFilter[] = [
    {
      initialState: {},
      render: () => null,
      addToRequest: () => {},
      validate: (request) => {
        if (!request.fromDate || !request.toDate) {
          return "Please select a date range";
        }
        return null;
      },
    },
    {
      initialState: {},
      render: () => null,
      addToRequest: () => {},
      validate: (request) => {
        if (!request.eldVehicleId) {
          return "Please select a vehicle";
        }
        return null;
      },
    },
  ];

  const handleRun = () => {
    if (!fromDate || !toDate) {
      enqueueSnackbar("Please select a date range", { variant: "error" });
      return;
    }
    if (!eldVehicleId) {
      enqueueSnackbar("Please select a vehicle", { variant: "error" });
      return;
    }

    const request: Record<string, any> = {
      ran: true,
      fromDate,
      toDate,
      eldVehicleId,
    };
    
    if (usState) {
      request.usState = usState;
    }

    currentRequest.current = request;
    setShouldRefresh(true);
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="History Lines Report">
          <DownloadExcelButton baseUrl={endpoints.reports.historyLines.excel} filters={filters} currentRequest={currentRequest} />
        </PageHeader>

        <Alert
          icon={<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}><Iconify icon="mdi:robot-outline" /></Box>}
          severity="success"
          sx={{ mb: 2 }}
        >
          <AlertTitle>AI-Powered Mileage Recovery</AlertTitle>
          When the ELD is temporarily disconnected, our system detects missing miles using odometer readings and intelligently distributes them across states based on your recorded travel patterns. This ensures your history lines report captures all miles driven.
        </Alert>

        <Card sx={{ mb: 2 }}>
          <Stack gap={1} direction="row" flexWrap="wrap" alignItems="center" sx={{ p: 2 }}>
            <DatePicker
              label="From Date"
              value={!fromDate ? null : dayjs(fromDate)}
              onChange={(date: Dayjs | null) => setFromDate(date?.format("YYYY-MM-DD") || null)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small"
                },
              }}
              sx={{ maxWidth: { md: 180 } }}
            />
            <DatePicker
              label="To Date"
              value={!toDate ? null : dayjs(toDate)}
              onChange={(date: Dayjs | null) => setToDate(date?.format("YYYY-MM-DD") || null)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small"
                },
              }}
              sx={{ maxWidth: { md: 180 } }}
            />
            <TextField
              select
              label="Vehicle"
              value={eldVehicleId || ""}
              onChange={(e) => setEldVehicleId(e.target.value ? Number(e.target.value) : null)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              {vehicles.map((vehicle) => (
                <MenuItem key={vehicle.id} value={vehicle.id}>
                  {vehicle.vehicleUnitNumber}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="State"
              value={usState}
              onChange={(e) => setUsState(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
              disabled={!eldVehicleId || !fromDate || !toDate || statesLoading}
              InputProps={{
                endAdornment: statesLoading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null,
              }}
            >
              <MenuItem value="">All States</MenuItem>
              {states.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="contained" onClick={handleRun} disabled={loading}>
              Run Report
            </Button>
          </Stack>
        </Card>

        {data && (data.lines?.length || 0) > 0 && <DeviceHistoryLineReport deviceHistoryLines={data.lines || []} />}
        {loaded && data && data.lines?.length === 0 && <NoneFoundReport />}
      </DisplayPageData>
    </Container>
  );
}
